import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Capacitor } from '@capacitor/core';

interface DemoModeContextType {
  isDemoMode: boolean;
  demoGamesPlayed: number;
  maxDemoGames: number;
  startDemo: () => void;
  endDemo: () => void;
  incrementDemoGames: () => void;
  canPlayDemo: boolean;
}

const DemoModeContext = createContext<DemoModeContextType | undefined>(undefined);

export function DemoModeProvider({ children }: { children: ReactNode }) {
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [demoGamesPlayed, setDemoGamesPlayed] = useState(0);
  const maxDemoGames = 2;
  const isNativePlatform = Capacitor.isNativePlatform();

  // Load demo state from localStorage ONLY on desktop (not native mobile apps)
  useEffect(() => {
    // Demo mode should only work on desktop, not in native mobile apps
    if (isNativePlatform) {
      // Clear any existing demo state on native platforms
      localStorage.removeItem('demo_state');
      setIsDemoMode(false);
      setDemoGamesPlayed(0);
      return;
    }

    const savedDemoState = localStorage.getItem('demo_state');
    if (savedDemoState) {
      const { gamesPlayed, started } = JSON.parse(savedDemoState);
      setDemoGamesPlayed(gamesPlayed || 0);
      setIsDemoMode(started || false);
    }
  }, [isNativePlatform]);

  // Save demo state to localStorage ONLY on desktop (not native mobile apps)
  useEffect(() => {
    // Don't save demo state on native platforms
    if (isNativePlatform) {
      return;
    }

    if (isDemoMode || demoGamesPlayed > 0) {
      localStorage.setItem('demo_state', JSON.stringify({
        gamesPlayed: demoGamesPlayed,
        started: isDemoMode
      }));
    }
  }, [isDemoMode, demoGamesPlayed, isNativePlatform]);

  const startDemo = () => {
    // Only allow demo mode on desktop, not native mobile apps
    if (!isNativePlatform) {
      setIsDemoMode(true);
    }
  };

  const endDemo = () => {
    setIsDemoMode(false);
  };

  const incrementDemoGames = () => {
    setDemoGamesPlayed(prev => prev + 1);
  };

  const canPlayDemo = demoGamesPlayed < maxDemoGames;

  return (
    <DemoModeContext.Provider
      value={{
        isDemoMode,
        demoGamesPlayed,
        maxDemoGames,
        startDemo,
        endDemo,
        incrementDemoGames,
        canPlayDemo
      }}
    >
      {children}
    </DemoModeContext.Provider>
  );
}

export function useDemoMode() {
  const context = useContext(DemoModeContext);
  if (context === undefined) {
    throw new Error('useDemoMode must be used within a DemoModeProvider');
  }
  return context;
}

